import { useState } from "react"
import { Link, useLocation } from "react-router-dom"
import {
  LayoutDashboard,
  Settings,
  Users,
  Clock,
  FileText,
  Archive,
  History,
  CornerDownRight,
  Menu,
  ChevronDown,
  ChevronRight,
  MapPin,
  HardHat,
  User,
  Fingerprint,
  NotebookPen,
  CalendarDays
} from "lucide-react"
import "../Styles/font.css"

const menuItems = [
  { name: "Dashboard", path: "/dashboard", icon: <LayoutDashboard size={20} /> },
  { name: "Client Configuration", path: "/client-config", icon: <Settings size={20} /> },
  {
    name: "Masters",
    icon: <Users size={20} />,
    subMenu: [
      { name: "Site Master", path: "/masters/site", icon: <MapPin size={16} /> },
      { name: "Contractor Master", path: "/masters/contractor", icon: <HardHat size={16} /> },
      { name: "Employee Master", path: "/masters/employee", icon: <User size={16} /> },
    ],
  },
  { name: "Holiday Master", path: "/holidays", icon: <CalendarDays size={20} /> },
  { name: "Attendance-Master", path: "/attendance", icon: <Fingerprint size={20} /> },
  { name: "Attendance-Summary", path: "/attendance-records", icon: <NotebookPen size={20} /> },
  { name: "Triggered", path: "/triggered", icon: <Clock size={20} /> },
  { name: "Processed", path: "/processed", icon: <FileText size={20} /> },
  { name: "Revision", path: "/revision", icon: <History size={20} /> },
  { name: "Returns", path: "/returns", icon: <CornerDownRight size={20} /> },
  { name: "Repository", path: "/repository", icon: <Archive size={20} /> },
  { name: "Service Certificate", path: "/service-certificate", icon: <FileText size={20} /> },
]

const Sidebar = ({ roleId }) => {
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(false)
  const [openSubmenus, setOpenSubmenus] = useState({})

  // Get company name from sessionStorage (supports common keys)
  const getCompanyName = () => {
    const possibleKeys = ["user", "auth", "userInfo", "profile", "company", "companyName", "company_name"]
    for (const key of possibleKeys) {
      const data = sessionStorage.getItem(key)
      if (data) {
        try {
          const parsed = JSON.parse(data)
          const name = parsed.companyName || parsed.company_name || parsed.company || parsed.name || ""
          if (name) return name
        } catch (e) {
          if (data.toLowerCase().includes("dusters") || data.toLowerCase().includes("tickbox")) {
            return data
          }
        }
      }
    }
    return ""
  }

  const companyName = getCompanyName()
  const isDustersOrTickbox = companyName && 
    (companyName.toLowerCase().includes("dusters") || companyName.toLowerCase().includes("tickbox"))

  const toggleSubmenu = (name) => {
    setOpenSubmenus(prev => ({
      ...prev,
      [name]: !prev[name]
    }))
  }

  const filteredMenuItems = menuItems.filter((item) => {
    if (roleId === "20") {
      return ["Attendance-Master", "Attendance-Summary", "Holiday Master"].includes(item.name)
    }

    if (item.name === "Client Configuration" && roleId !== "1") return false
    if (item.name === "Triggered" && roleId !== "2") return false
    if (item.name === "Holiday Master" && roleId !== "20") return false
    if (item.name === "Attendance-Summary" && roleId !== "20") return false
    if (item.name === "Attendance-Master" && roleId !== "20") return false
    if (item.name === "Processed" && roleId !== "2") return false
    if (item.name === "Revision" && roleId !== "2") return false
    if (item.name === "Returns" && !["2", "3"].includes(roleId)) return false

    // SERVICE CERTIFICATE: role 2 or 3 + company has Dusters or Tickbox
    if (item.name === "Service Certificate") {
      return ["2", "3"].includes(roleId) && isDustersOrTickbox
    }

    return true
  })

  return (
    <div
      className={`h-screen sticky top-0 bg-white/10 backdrop-blur-xl border-r border-white/30 shadow-2xl transition-all duration-300 ${
        collapsed ? "w-20" : "w-64"
      }`}
    >
      <div className="flex items-center justify-between px-4 py-5">
        {!collapsed && (
          <h1 className="text-3xl font-bicubik text-white tracking-wide">
            TICKB<span className="text-[#d97925] font-extrabold animate-pulse">▢</span>X
          </h1>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="text-white p-2 hover:bg-white/20 rounded transition"
        >
          <Menu size={20} />
        </button>
      </div>

      <ul className="space-y-2 mt-4">
        {filteredMenuItems.map((item) => {
          const isActive = location.pathname === item.path

          return (
            <li key={item.name}>
              {item.subMenu ? (
                <div>
                  <button
                    onClick={() => toggleSubmenu(item.name)}
                    className={`flex w-full items-center justify-between px-4 py-2 rounded-xl transition-all ${
                      openSubmenus[item.name] ? "bg-[#d97925] text-white" : "text-white hover:bg-white/20"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {item.icon}
                      {!collapsed && <span className="text-sm font-medium">{item.name}</span>}
                    </div>
                    {!collapsed && (
                      <span>{openSubmenus[item.name] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</span>
                    )}
                  </button>

                  {openSubmenus[item.name] && !collapsed && (
                    <ul className="ml-10 mt-2 space-y-1">
                      {item.subMenu.map((subItem) => {
                        const isSubActive = location.pathname === subItem.path
                        return (
                          <li key={subItem.path}>
                            <Link
                              to={subItem.path}
                              className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm transition-all ${
                                isSubActive ? "bg-[#d97925] text-white" : "text-white hover:bg-white/20"
                              }`}
                            >
                              {subItem.icon}
                              <span>{subItem.name}</span>
                            </Link>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>
              ) : (
                <Link
                  to={item.path}
                  onClick={() => setOpenSubmenus({})}
                  className={`flex items-center gap-3 px-4 py-2 rounded-xl transition-all ${
                    isActive ? "bg-[#d97925] text-white" : "text-white hover:bg-white/20"
                  }`}
                >
                  {item.icon}
                  {!collapsed && <span className="text-sm font-medium">{item.name}</span>}
                </Link>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default Sidebar